import { useState, useEffect, useMemo } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { blogPosts } from "@/data/blogPosts";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, User, ArrowRight, Search, ArrowLeft, X } from "lucide-react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { blogTopicArchivePath, getBlogTopicArchiveByTag } from "@/lib/blogTopicArchives";

export default function BlogSearchResults() {
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const q = params.get("q") || "";
    setQuery(q);
    setInputValue(q);
  }, [searchString]);

  const results = useMemo(() => {
    if (query.trim().length < 2) return [];
    const q = query.trim().toLowerCase();
    return blogPosts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q)) ||
        p.content.toLowerCase().includes(q)
    );
  }, [query]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = inputValue.trim();
    if (q) {
      setLocation(`/blog/search?q=${encodeURIComponent(q)}`);
    }
  };

  const clearSearch = () => {
    setInputValue("");
    setLocation("/blog");
  };

  /** Highlight matching text in a string */
  function highlight(text: string, q: string): React.ReactNode {
    if (!q || q.length < 2) return text;
    const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-amber-200/60 text-amber-900 rounded-sm px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  }

  /** Get a short excerpt snippet around the first match */
  function getSnippet(content: string, q: string): string {
    if (!q) return "";
    const idx = content.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return "";
    const start = Math.max(0, idx - 80);
    const end = Math.min(content.length, idx + q.length + 120);
    const snippet = content.slice(start, end).replace(/<[^>]+>/g, "");
    return (start > 0 ? "..." : "") + snippet + (end < content.length ? "..." : "");
  }

  return (
    <>
      <SEOHead
        title={query ? `Search: "${query}" | Integrated.Social Blog` : "Search Articles | Integrated.Social Blog"}
        description="Search across all AI marketing, AEO, Agentic AI, and B2B growth articles on Integrated.Social."
        canonical="https://integrated.social/blog/search/"
        noindex={true}
      />
      <Header />
      <main className="min-h-screen bg-background pt-24">
        {/* Search Header */}
        <section className="py-12 md:py-16 border-b border-border">
          <div className="container max-w-4xl mx-auto px-4">
            <Link href="/blog/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to all articles
            </Link>

            <h1 className="font-serif text-3xl md:text-5xl tracking-tight text-foreground mb-6">
              {query ? (
                <>Search results for <span className="text-amber-600">"{query}"</span></>
              ) : (
                "Search articles"
              )}
            </h1>

            {/* Search form */}
            <form onSubmit={handleSearch} className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
              <input
                type="search"
                autoFocus
                placeholder="Search AEO, Agentic AI, AI Governance, PPC, B2B GTM..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full pl-12 pr-12 py-3.5 text-base rounded-xl border border-border bg-muted/60 focus:bg-background focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </form>

            {query.trim().length >= 2 && (
              <p className="mt-4 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{results.length}</span>{" "}
                {results.length === 1 ? "article" : "articles"} found
              </p>
            )}
          </div>
        </section>

        {/* Results */}
        <section className="py-12 md:py-16">
          <div className="container max-w-4xl mx-auto px-4">
            {query.trim().length < 2 ? (
              /* Empty / too-short state */
              <div className="text-center py-16">
                <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground text-lg mb-2">Type at least 2 characters to search.</p>
                <p className="text-muted-foreground text-sm">
                  Try: <button onClick={() => { setInputValue("AEO"); setLocation("/blog/search?q=AEO"); }} className="text-amber-600 hover:underline">AEO</button>,{" "}
                  <button onClick={() => { setInputValue("Agentic AI"); setLocation("/blog/search?q=Agentic+AI"); }} className="text-amber-600 hover:underline">Agentic AI</button>,{" "}
                  <button onClick={() => { setInputValue("AI Governance"); setLocation("/blog/search?q=AI+Governance"); }} className="text-amber-600 hover:underline">AI Governance</button>
                </p>
              </div>
            ) : results.length === 0 ? (
              /* No results */
              <div className="text-center py-16">
                <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-foreground font-semibold text-xl mb-2">No articles found for "{query}"</p>
                <p className="text-muted-foreground text-sm mb-6">Try a broader term or browse by topic.</p>
                <Link href="/blog/" className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium">
                  <ArrowLeft className="w-4 h-4" /> Browse all articles
                </Link>
              </div>
            ) : (
              /* Results list */
              <div className="grid gap-8 md:gap-10">
                {results.map((post, index) => {
                  const snippet = getSnippet(post.content, query);
                  return (
                    <article
                      key={post.slug}
                      className="group md:grid md:grid-cols-5 md:gap-8 border-b border-border pb-8 md:pb-10"
                    >
                      {/* Image */}
                      <div className="md:col-span-2 mb-4 md:mb-0">
                        <Link href={`/blog/${post.slug}/`}>
                          <div className="aspect-[16/10] overflow-hidden rounded-lg bg-muted">
                            <ResponsiveImage
                              src={post.heroImage}
                              alt={post.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              width={640}
                              height={400}
                              sizes="(min-width: 768px) 40vw, calc(100vw - 32px)"
                              priority={index === 0}
                            />
                          </div>
                        </Link>
                      </div>

                      {/* Content */}
                      <div className="md:col-span-3 flex flex-col justify-center">
                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {post.tags.slice(0, 3).map((tag) => (
                            <Link
                              key={tag}
                              href={getBlogTopicArchiveByTag(tag) ? blogTopicArchivePath(getBlogTopicArchiveByTag(tag)!) : `/blog/?tag=${encodeURIComponent(tag)}`}
                              className="text-xs font-medium px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 transition-colors"
                            >
                              {tag}
                            </Link>
                          ))}
                        </div>

                        {/* Title with highlight */}
                        <Link href={`/blog/${post.slug}/`}>
                          <h2 className="font-serif text-xl md:text-2xl text-foreground group-hover:text-amber-600 transition-colors duration-200 mb-3 leading-tight">
                            {highlight(post.title, query)}
                          </h2>
                        </Link>

                        {/* Excerpt or content snippet */}
                        <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-4 line-clamp-3">
                          {snippet
                            ? highlight(snippet, query)
                            : highlight(post.excerpt, query)}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            {post.author}
                          </span>
                          <Link href={`/blog/${post.slug}/`} className="ml-auto flex items-center gap-1 font-medium text-amber-600 hover:text-amber-700 transition-colors">
                            Read article <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
